import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { MapContainer, TileLayer, CircleMarker, Marker, Polyline, Tooltip, Popup, Pane, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CrudService from "services/cruds-service";
import { formatDate } from "./oceanStatus/constants";

/*
 * Ocean Shipment Status map: POL -> transshipments -> POD from UN/LOCODE,
 * the carrier's last reported position, and the vessel's live AIS position
 * (Sinay pilot). Straight lines between ports are indicative, not the sea route.
 */

const VESSEL_STATUS_TEXT = {
    OK: "Live AIS position",
    CACHED: "Live AIS position",
    STALE: "Last known AIS position (provider currently unavailable)",
    NOT_CONFIGURED: "Live vessel position is not connected yet (AIS provider pending).",
    NO_IMO: "Live vessel position needs the vessel IMO number from the carrier.",
    NOT_FOUND: "The AIS provider has no recent position for this vessel.",
    WAITING:
        "Waiting for the vessel's next AIS signal. Coverage is coastal: mid-ocean positions can arrive hours or days late.",
    ERROR: "The AIS provider could not be reached.",
    ARRIVED: "Vessel has arrived at POD; live position is no longer tracked.",
    COMPLETED: "Shipment completed; live position is no longer tracked.",
};

// Türkiye time, like every tracking screen.
const formatDateTime = (value) => (value ? `${formatDate(value, true)} TRT` : "-");

// Ship glyph pointing north, rotated to the AIS course.
const vesselIcon = (course) =>
    L.divIcon({
        className: "",
        iconSize: [30, 30],
        iconAnchor: [15, 15],
        html: `<div style="transform: rotate(${Number(course) || 0}deg); width:30px; height:30px; display:flex; align-items:center; justify-content:center; border-radius:50%; background:rgba(255,255,255,0.85); box-shadow:0 0 0 3px rgba(26,115,232,0.25), 0 2px 6px rgba(15,23,42,0.35);">
            <svg width="18" height="22" viewBox="0 0 22 28" xmlns="http://www.w3.org/2000/svg">
              <path d="M11 1 L20 24 L11 19 L2 24 Z" fill="#1a73e8" stroke="#ffffff" stroke-width="2" stroke-linejoin="round"/>
            </svg></div>`,
    });

const carrierIcon = L.divIcon({
    className: "",
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    html: '<div style="width:14px;height:14px;border-radius:50%;background:#f59e0b;border:2px solid #fff;box-shadow:0 0 0 2px rgba(245,158,11,0.35)"></div>',
});

const FitBounds = ({ points }) => {
    const map = useMap();
    useEffect(() => {
        if (points.length === 1) map.setView(points[0], 6);
        else if (points.length > 1) map.fitBounds(points, { padding: [36, 36], maxZoom: 7 });
    }, [map, points]);
    return null;
};

function OceanShipmentMap({ trackingId, refreshKey }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError("");
        CrudService.getOceanTrackingMap(trackingId)
            .then((response) => !cancelled && setData(response?.data || null))
            .catch((err) => !cancelled && setError(err?.errors?.[0]?.detail || "Map data could not be loaded."))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [trackingId, refreshKey]);

    const stops = useMemo(() => (data?.stops || []).filter((s) => s.latitude != null && s.longitude != null), [data]);
    const vessel = data?.vessel?.position || null;
    const carrier = data?.carrier || null;

    const points = useMemo(() => {
        const list = stops.map((s) => [s.latitude, s.longitude]);
        if (vessel) list.push([vessel.latitude, vessel.longitude]);
        if (carrier) list.push([carrier.latitude, carrier.longitude]);
        return list;
    }, [stops, vessel, carrier]);

    // Leg is "done" when the next stop has an actual event.
    const legs = useMemo(
        () =>
            stops.slice(1).map((stop, i) => ({
                key: `${stops[i].locode}-${stop.locode}`,
                positions: [
                    [stops[i].latitude, stops[i].longitude],
                    [stop.latitude, stop.longitude],
                ],
                done: stop.reached,
            })),
        [stops]
    );

    if (loading) {
        return (
            <MDBox display="flex" justifyContent="center" alignItems="center" height={320}>
                <CircularProgress size={26} />
            </MDBox>
        );
    }

    if (error || !points.length) {
        return (
            <MDBox py={3} textAlign="center">
                <MDTypography variant="caption" color="text">
                    {error || "No port coordinates are available for this route yet."}
                </MDTypography>
            </MDBox>
        );
    }

    const vesselStatus = data?.vessel?.status;
    let vesselNote = VESSEL_STATUS_TEXT[vesselStatus] || "";
    // AIS is coastal: say plainly when the last fix is old.
    const fixAt = vessel && new Date(vessel.positionAt || vessel.fetchedAt).getTime();
    const ageHours = fixAt ? (Date.now() - fixAt) / 3600000 : 0;
    if (vessel && ageHours > 6) {
        vesselNote = `Last AIS position (${ageHours < 48 ? `${Math.round(ageHours)}h` : `${Math.round(ageHours / 24)}d`} ago)`;
    }

    return (
        <MDBox>
            <MDBox sx={{ height: 320, borderRadius: "12px", overflow: "hidden", border: "1px solid #e5e7eb" }}>
                <MapContainer center={points[0]} zoom={4} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false} worldCopyJump>
                    <TileLayer
                        attribution="&copy; OpenStreetMap contributors"
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <FitBounds points={points} />

                    {legs.map((leg) => (
                        <Polyline
                            key={leg.key}
                            positions={leg.positions}
                            pathOptions={{
                                color: leg.done ? "#16a34a" : "#94a3b8",
                                weight: 3,
                                dashArray: leg.done ? null : "6 8",
                            }}
                        />
                    ))}

                    {stops.map((stop) => (
                        <CircleMarker
                            key={`${stop.role}-${stop.locode}`}
                            center={[stop.latitude, stop.longitude]}
                            radius={7}
                            pathOptions={{
                                color: "#ffffff",
                                weight: 2,
                                fillOpacity: 1,
                                fillColor: stop.reached ? "#16a34a" : "#64748b",
                            }}
                        >
                            <Tooltip direction="top" offset={[0, -6]} permanent>
                                <span style={{ fontSize: 11, fontWeight: 600 }}>
                                    {stop.role} · {stop.locode}
                                </span>
                            </Tooltip>
                            <Popup>
                                <strong>{stop.name}</strong> ({stop.locode})
                                <br />
                                {stop.role} · {stop.reached ? "reached" : "not reached yet"}
                                {stop.approximate && (
                                    <>
                                        <br />
                                        <em>Approximate location (city centre)</em>
                                    </>
                                )}
                            </Popup>
                        </CircleMarker>
                    ))}

                    {carrier && (
                        <Marker position={[carrier.latitude, carrier.longitude]} icon={carrierIcon}>
                            <Popup>
                                <strong>Carrier-reported position</strong>
                                <br />
                                {formatDateTime(carrier.at)}
                            </Popup>
                        </Marker>
                    )}

                    {/* Above Leaflet's tooltip pane (650) so port labels never hide the vessel. */}
                    <Pane name="vessel-pane" style={{ zIndex: 700 }}>
                    {vessel && (
                        <Marker
                            position={[vessel.latitude, vessel.longitude]}
                            icon={vesselIcon(vessel.course ?? vessel.heading)}
                        >
                            <Popup>
                                <strong>{data.vessel.vesselName || "Vessel"}</strong>
                                {data.vessel.imo && <> · IMO {data.vessel.imo}</>}
                                <br />
                                {vessel.speed != null && <>Speed {vessel.speed} kn · </>}
                                {vessel.course != null && <>Course {Math.round(vessel.course)}°</>}
                                {vessel.destination && (
                                    <>
                                        <br />
                                        Destination: {vessel.destination}
                                    </>
                                )}
                                <br />
                                Position at {formatDateTime(vessel.positionAt || vessel.fetchedAt)}
                            </Popup>
                        </Marker>
                    )}
                    </Pane>
                </MapContainer>
            </MDBox>

            <MDBox display="flex" flexWrap="wrap" alignItems="center" gap={2} mt={1}>
                <MDTypography variant="caption" color="text">
                    <span style={{ color: "#16a34a" }}>●</span> Reached &nbsp;
                    <span style={{ color: "#64748b" }}>●</span> Planned &nbsp;
                    <span style={{ color: "#f59e0b" }}>●</span> Carrier position &nbsp;
                    <span style={{ color: "#1a73e8" }}>▲</span> Vessel (AIS)
                </MDTypography>
                <MDTypography variant="caption" color={vessel ? "info" : "text"} fontWeight={vessel ? "medium" : "regular"}>
                    {vessel
                        ? `${vesselNote} · ${data.vessel.vesselName || ""} · ${formatDateTime(vessel.positionAt || vessel.fetchedAt)} · source ${vessel.source || data.vessel.provider}`
                        : vesselNote}
                </MDTypography>
            </MDBox>
            <MDTypography variant="caption" color="text" display="block" mt={0.5} sx={{ opacity: 0.8 }}>
                Lines between ports are indicative and do not show the actual sea route.
            </MDTypography>
        </MDBox>
    );
}

export default OceanShipmentMap;
